import { Router } from "@angular/router";
import { inject } from "@angular/core";
import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from "@angular/common/http";
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from "rxjs";

import { APIs } from "@azm/core/constants/api-names";
import { AuthService } from "@azm/core/services/auth";
import { ILoginResponse } from "@azm/core/interfaces/auth";
import { ToastService } from "@azm/core/services/toast/toast.service";
import { DialogService } from "@azm/shared/components/overlay/dialog/services/dialog.service";

let refreshTokenRequest$: Observable<ILoginResponse | null> | null = null;

export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
	const router = inject(Router);
	const dialog = inject(DialogService);
	const toast = inject(ToastService);
	const authService = inject(AuthService);

	const handleAuthFailure = (error: HttpErrorResponse) => {
		toast.clear();
		authService.clear();
		dialog.closeAll();
		router.navigate(["/"]);
		return throwError(() => error);
	};

	return next(req).pipe(
		catchError((error: HttpErrorResponse) => {
			if (error && error.status === HttpStatusCode.Unauthorized) {
				const url = req.url;
				const urlLower = url.toLowerCase();
				const skipRefreshForAnonymousAuth =
					urlLower.includes("token-access") ||
					urlLower.includes("/auth/login") ||
					urlLower.includes("/auth/register") ||
					urlLower.includes("/auth/callback");
				if (skipRefreshForAnonymousAuth) {
					return throwError(() => error);
				}

				if (req.url.includes(APIs.auth.refreshToken) || !authService.refreshToken) {
					return handleAuthFailure(error);
				}

				if (!refreshTokenRequest$) {
					refreshTokenRequest$ = authService.refreshTheToken().pipe(
						shareReplay(1),
						finalize(() => {
							refreshTokenRequest$ = null;
						}),
					);
				}

				return refreshTokenRequest$.pipe(
					switchMap((data) => {
						if (!data) {
							return throwError(() => error);
						}

						return next(
							req.clone({
								setHeaders: {
									Authorization: `Bearer ${data.accessToken}`,
								},
							}),
						);
					}),
					catchError((refreshError: HttpErrorResponse) => handleAuthFailure(refreshError)),
				);
			}

			return throwError(() => error);
		}),
	);
};
